import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import "./ReportCentre.css";
import {
  FileDownload as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Tab,
  Link,
  Select,
  MenuItem,
} from "@mui/material";
import RightSideBar from "../../components/Rightsidebar/RightSideBar";
import Pagination from "../../components/DynamicTable/Pagination";
import * as _customreports from "../../_redux/_services/CustomReportsSlice";
import * as _props from "../../_redux/_services/JobPostingSlice";
import * as _clientser from "../../_redux/_services/ClientsSlice";
import { DownloadCustomReport } from "../../_redux/_apiHelpers/ReportsApihelper";
import { updateSnackbar } from "../../_redux/SnackbarSlice";
import { GetUserId, GetUserInfo } from "../../_utilities/getUserInfo";
import { format } from "date-fns";
import dayjs from "dayjs";
import EditScheduleReport from "./ScheduleReportEdit";
import FloatingMenu from "../../components/FloatingMenu/FloatingMenu";
import { ReportCentrefloatingMenuOptions } from "../../constants/floatingMenuConstants";
import { getfields } from "./ReportConfig";
import { reportTypesEnum } from "../../constants/constants";

const ReportCentre = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("generatedReports");
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [userId, setUserId] = useState(null);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saveReports, setSaveReports] = useState([]);
  // const [paginatedSavedReports, setPaginatedSavedReports] = useState([]);
  const [editReportDialogOpen, setEditReportDialogOpen] = useState(false);
  const [editReportData, setEditReportData] = useState(null);

  const storedTheme = JSON.parse(localStorage.getItem("themeSettings")) || "";

  // Check if navigated from report submission via URL param
  useEffect(() => {
    if (searchParams.get("submitted") === "true") {
      setShowInfoBanner(true);
      setTab('generatedReports');
      setSearchParams({}, { replace: true });
    } else if (searchParams.get("skipped") === "true") {
      setShowInfoBanner(false);
      setTab('savedReports');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const reportPayload = useSelector((state) => state.ReportData);

  useEffect(() => {
    if (reportPayload !== null && reportPayload?.data) {
      setSaveReports([reportPayload?.data]);
    }
  }, [reportPayload]);

  // Auto-dismiss info banner after 10 seconds
  useEffect(() => {
    if (showInfoBanner) {
      const timer = setTimeout(() => {
        setShowInfoBanner(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showInfoBanner]);

  // Redux selector for reports data
  const reportsData = useSelector((state) => state.GetCustomReports);

  // Redux selectors for filter lookup data
  const recruiterList = useSelector(
    (state) => state.GetManagerTeamlead.recruiter,
  );
  const clientList = useSelector((state) => state.FilterClients.data);
  const jobsList = useSelector((state) => state.Listjobpostings.data);

  // Lookup maps for filter values
  const [recruiterMap, setRecruiterMap] = useState({});
  const [clientMap, setClientMap] = useState({});
  const [jobsMap, setJobsMap] = useState({});

  // Fetch user ID on mount
  useEffect(() => {
    async function fetchUserId() {
      const userInfo = await GetUserInfo();
      const id = userInfo?.[0]?.userId;
      if (id) {
        setUserId(id);
      }
    }
    fetchUserId();
  }, []);

  // Fetch reports when userId is available
  useEffect(() => {
    if (userId) {
      dispatch(_customreports.getCustomReportsAsync(userId));
    }
  }, [dispatch, userId]);

  // Fetch lookup data for filter values (recruiters, clients, jobs)
  useEffect(() => {
    async function fetchLookupData() {
      const id = await GetUserId();
      if (id) {
        dispatch(_props.getManagerTeamleadAsync(" ", id));
        dispatch(_clientser.filterClientsAsync(""));
        dispatch(
          _props.listjobpostingsAsync({ limit: 1000, offset: 1 }, "list"),
        );
      }
    }
    fetchLookupData();
  }, [dispatch]);

  // Process recruiter list into lookup map
  useEffect(() => {
    if (recruiterList && recruiterList.length > 0) {
      const map = {};
      recruiterList.forEach((recruiter) => {
        map[recruiter.id.toString()] =
          `${recruiter.firstName} ${recruiter.lastName}`;
      });
      setRecruiterMap(map);
    }
  }, [recruiterList]);

  // Process client list into lookup map
  useEffect(() => {
    if (clientList && clientList.length > 0) {
      const map = {};
      clientList.forEach((client) => {
        map[client.id.toString()] = client.clientName;
      });
      setClientMap(map);
    }
  }, [clientList]);

  // Process jobs list into lookup map
  useEffect(() => {
    if (jobsList && jobsList.rows && jobsList.rows.length > 0) {
      const map = {};
      jobsList.rows.forEach((job) => {
        map[job.id.toString()] = job.jobCode
          ? `${job.jobTitle} (${job.jobCode})`
          : job.jobTitle;
      });
      setJobsMap(map);
    }
  }, [jobsList]);

  // Get reports array from response - API returns { success, results, pagination }
  const reports = reportsData?.data?.results || [];

  // Pagination calculations
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = reports.slice(startIndex, endIndex);

  // Reset to page 1 when reports change
  useEffect(() => {
    setCurrentPage(1);
  }, [reports.length]);

  // Format report type for display
  const formatReportType = (type) => {
    switch (type) {
      case "candidate":
        return "Candidate Report";
      case "job":
        return "Job Report";
      case "client":
        return "Client Report";
      default:
        return type
          ? type.charAt(0).toUpperCase() + type.slice(1) + " Report"
          : "Report";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  // Format status for display
  const formatStatus = (status) => {
    if (!status) return "Pending";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const [downloadingId, setDownloadingId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Format field name for display (camelCase to Title Case)
  const formatFieldName = (field, type) => {
    if (!field) return "";
    // Strip "C:" prefix from custom fields - show only the custom field name
    let displayField = field;
    if (typeof field === "string" && field.startsWith("C:")) {
      displayField = field.slice(2).trim();
    }

    if (type === "candidate") {
      // Handle specific field mappings
      const fieldMappings = {
        candidateId: "Applicant Code",
        firstName: "First Name",
        lastName: "Last Name",
        contactNumber: "Contact Number",
        alternateContactNumber: "Alternate Contact",
        emailId: "Email",
        linkedIn: "LinkedIn",
        customField: "Custom Field",
        associatedJob: "Associated Job",
        jobId: "Job Code",
        jobTitle: "Job Title",
        resume: "Resume",
        client: "Client",
        associatedDate: "Associated Date",
        candidateStatus: "Candidate Status",
        sourcedFrom: "Sourced From",
        createdAt: "Created At",
        updatedAt: "Updated At",
      };
      const mapped = fieldMappings[displayField] || fieldMappings[field];
      if (mapped) return mapped;
      // For custom fields (after stripping C:), format the name for display
      return displayField
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());
    } else if (type === 'job') {
      let filterName = getfields(reportTypesEnum.JOB).jobFields.find((item) => item.mappingName === field)
      return filterName ? filterName.displayName : displayField;
    }
  };

  // Format filter type for display
  const formatFilterType = (filterType) => {
    const filterMappings = {
      recruiter: "Recruiter",
      client: "Client",
      status: "Status",
      source: "Source",
      jobTitle: "Job Title",
      location: "Location",
    };
    return (
      filterMappings[filterType] ||
      filterType
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
    );
  };

  // Format filter operation for display
  const formatFilterOperation = (operation) => {
    const operationMappings = {
      is: "Is",
      in: "In",
      not_in: "Not In",
      equals: "Is",
      not_equals: "Not In",
      contains: "Contains",
      startsWith: "Starts With",
      endsWith: "Ends With",
      greaterThan: "Greater Than",
      lessThan: "Less Than",
    };
    return operationMappings[operation] || operation;
  };

  // Get display name for filter value based on filter type
  const getFilterValueDisplayName = (filterType, value) => {
    const valueStr = value?.toString() || value;
    switch (filterType) {
      case "recruiter":
      case "uploadedBy":
      case "processedBy":
      case "hiringManager":
      case "assignedRecruiter":
        return recruiterMap[valueStr] || valueStr;
      case "client":
      case "clientName":
        return clientMap[valueStr] || valueStr;
      case "job":
        return jobsMap[valueStr] || valueStr;
      default:
        return valueStr;
    }
  };

  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedReport(null);
  };

  const handleDownload = async (report) => {
    if (report.status === "completed" && report.s3BucketUrl) {
      try {
        setDownloadingId(report.id);

        // Call download API with S3 key
        const response = await DownloadCustomReport(report.s3BucketUrl);

        // Create blob from response
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Extract filename from s3BucketUrl or use report name
        const fileName =
          report.s3BucketUrl.split("/").pop() || `${report.reportName}.xlsx`;
        link.setAttribute("download", fileName);

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        dispatch(
          updateSnackbar({
            type: "success",
            message: "Report downloaded successfully",
          }),
        );
      } catch (error) {
        dispatch(
          updateSnackbar({
            type: "error",
            message: "Failed to download report. Please try again.",
          }),
        );
      } finally {
        setDownloadingId(null);
      }
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const handleDeleteConfirm = () => {
    if (deleteId && userId) {
      dispatch(_customreports.deleteCustomReportAsync(deleteId, userId));
    }
    handleDeleteClose();
  };

  const handleRefresh = () => {
    if (userId) {
      dispatch(_customreports.getCustomReportsAsync(userId));
    }
  };

  const handleEdit = (report) => {
    setEditReportDialogOpen(true);
    setEditReportData(report);
  };

  const handleEditClose = () => {
    setEditReportDialogOpen(false);
    setEditReportData(null);
  };

  const handleReceiveActionHandler = (type) => {
    if (type === "scheduleReport") {
      navigate("/ats/layout/reports/schedulereport");
    } else if (type === "instantReports") {
      navigate("/ats/layout/reports/dashboard");
    }
  };

  return (
    <div className="report-centre-container">
      {/* Header Section */}
      <div className="report-centre-header">
        <div className="header-left">
          <div className="header-icon">
            <DescriptionIcon />
          </div>
          <div className="header-content">
            <h1 className="header-title">Report Centre</h1>
            <p className="header-subtitle">
              View and Download all the generated reports in one place
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="create-report-btn"
            onClick={() => navigate("/ats/layout/reports/dashboard")}
          >
            + Create Report
          </button>
          <IconButton onClick={handleRefresh} title="Refresh reports">
            <RefreshIcon />
          </IconButton>
          {/* <FloatingMenu
            floatingMenuOptions={ReportCentrefloatingMenuOptions}
            onReceiveActionHandler={handleReceiveActionHandler}
            storedTheme={storedTheme}
        /> */}
        </div>
      </div>

      {/* Info Banner */}
      {showInfoBanner && (
        <div className="report-info-banner">
          <p>
            Report submitted. It's being generated and may take some time. Check
            Status when it's ready
          </p>
          <IconButton
            size="small"
            onClick={() => setShowInfoBanner(false)}
            style={{ color: "#4caf50" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      {/* tabs */}
      <TabContext value={tab}>
        <Box>
          <TabList
            onChange={(_, newValue) => { setTab(newValue) }}
            sx={{
              mt: 1,
              border: "none",
              "& .MuiTabs-flexContainer": {
                justifyContent: "center",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minWidth: "auto",
                fontSize: { xs: "12px", sm: "14px" },
              },
              "& .Mui-selected": {
                bgcolor: storedTheme.primaryColor,
                borderRadius: 5,
                color: "#000 !important",
              },
            }}
          >
            {/* <Tab value="generatedReports" label="Generated Reports" />
            <Tab value="savedReports" label="Saved Reports" /> */}
          </TabList>
        </Box>
        <TabPanel value="generatedReports">
          <div className="generated-report-header">
            {reports && !reportsData?.isLoading && (
              <p className="reports-total-count-top">
                <b>Total Reports</b> - {reports.length}
              </p>
            )}
            {/* <IconButton onClick={handleRefresh} title="Refresh reports">
              <RefreshIcon />
            </IconButton> */}
          </div>
          <div
            className={`reports-table-container${reports.length > 0 ? " reports-table-container--has-reports" : ""}`}
          >
            {reportsData?.isLoading ? (
              <div className="loading-container">
                <CircularProgress />
              </div>
            ) : reports.length === 0 ? (
              <div className="no-reports-message">
                <p>
                  No reports found. Create a new report from the Reports Dashboard.
                </p>
              </div>
            ) : (
              <div className="reports-table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Report Name</th>
                      <th>Category</th>
                      <th>Generated Date</th>
                      <th>Created By</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReports.map((report) => (
                      <tr key={report.id}>
                        <td className="report-name-cell">
                          {report.reportName || `Report_${report.id}`}
                        </td>
                        <td>{formatReportType(report.reportType)}</td>
                        <td>{formatDate(report.createdAt)}</td>
                        <td>{report.createdBy || "-"}</td>
                        <td>
                          <span
                            className={`status-badge ${(report.status || "pending").toLowerCase()}`}
                          >
                            {formatStatus(report.status)}
                          </span>
                        </td>
                        <td className="action-cell">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(report)}
                            className="action-icon"
                            title="View report info"
                          >
                            <InfoIcon style={{ color: "#3b4046" }} fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(report)}
                            disabled={
                              report.status !== "completed" ||
                              downloadingId === report.id
                            }
                            className={`action-icon ${report.status !== "completed" ? "disabled" : ""}`}
                            title={
                              report.status === "completed"
                                ? "Download report"
                                : "Report not ready"
                            }
                          >
                            {downloadingId === report.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(report.id)}
                            className="action-icon"
                            title="Delete report"
                          >
                            <DeleteIcon style={{ color: "#3b4046" }} fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination - at bottom of page */}

              </div>

            )}
          </div>
          <div
            className="reports-pagination-footer"
            style={{ justifyContent: "flex-end" }}
          >
            <div className="pagingRight">
              <Pagination
                className="pagination-bar"
                currentPage={currentPage}
                totalCount={reports.length}
                pageSize={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
              <Select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="selectData"
              >
                <MenuItem className="selectOptions" value={10}>
                  10
                </MenuItem>
                <MenuItem className="selectOptions" value={20}>
                  20
                </MenuItem>
                <MenuItem className="selectOptions" value={50}>
                  50
                </MenuItem>
              </Select>
            </div>
          </div>
        </TabPanel>
        <TabPanel value="savedReports">
          <div className="generated-report-header">
            {saveReports && (
              <p className="reports-total-count-top">
                <b>Total Reports</b> - {saveReports.length}
              </p>
            )}
            <IconButton onClick={handleRefresh} title="Refresh reports">
              <RefreshIcon />
            </IconButton>
          </div>
          <div
            className={`reports-table-container${saveReports.length > 0 ? " reports-table-container--has-reports" : ""}`}
          >
            {saveReports?.isLoading ? (
              <div className="loading-container">
                <CircularProgress />
              </div>
            ) : saveReports.length === 0 ? (
              <div className="no-reports-message">
                <p>
                  No reports found. Create a new report from the Reports Dashboard.
                </p>
              </div>
            ) : (
              <div className="reports-table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Report Name</th>
                      <th>Report Type</th>
                      <th>Frequency</th>
                      <th>Schedule</th>
                      <th>Duration</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saveReports.map((report) => (
                      <tr key={report.id}>
                        <td>
                          {report.reportName}
                        </td>
                        <td>{formatReportType(report.reportType)}</td>
                        <td>{report?.frequency ?? "NA"}</td>
                        <td>{dayjs(report?.scheduledTime, "HH:mm:ss").format("hh:mm A")}</td>
                        <td>
                          {report?.scheduleStartDate
                            ? `From ${dayjs(report.scheduleStartDate).format("MMM DD, YYYY")}${report.scheduleEndDate
                              ? ` To ${dayjs(report.scheduleEndDate).format("MMM DD, YYYY")}`
                              : " - No End Date"
                            }`
                            : "NA"}
                        </td>
                        <td>{"NA"}</td>
                        <td className="action-cell">
                          <IconButton
                            size="small"
                            className="action-icon"
                          >
                            <img src={process.env.PUBLIC_URL + '/assests/img/action.svg'} alt="action" width="15px" />
                          </IconButton>
                          <IconButton
                            size="small"
                            className="action-icon"
                            onClick={() => handleEdit(report)}
                          >
                            <img src={process.env.PUBLIC_URL + '/assests/img/edit-icon-dynamictable.svg'} alt="edit" />
                          </IconButton>
                          <IconButton
                            size="small"
                            className="action-icon"
                          >
                            <img src={process.env.PUBLIC_URL + '/assests/img/delete-icon-dynamictable.svg'} alt="delete" />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination - at bottom of page */}
                <div
                  className="reports-pagination-footer"
                  style={{ justifyContent: "flex-end" }}
                >
                  <div className="pagingRight">
                    <Pagination
                      className="pagination-bar"
                      currentPage={currentPage}
                      totalCount={reports.length}
                      pageSize={itemsPerPage}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                    <Select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="selectData"
                    >
                      <MenuItem className="selectOptions" value={10}>
                        10
                      </MenuItem>
                      <MenuItem className="selectOptions" value={20}>
                        20
                      </MenuItem>
                      <MenuItem className="selectOptions" value={50}>
                        50
                      </MenuItem>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabPanel>
      </TabContext>

      {/* Reports Table */}

      {/* Schedule Report Edit Side Drawer */}
      {editReportDialogOpen && editReportData && (
        <div className="schedule-report-edit-side-drawer">
          <RightSideBar
            componentLayout={{
              title: "Edit Report",
              description: "",
            }}
            componentData={
              <EditScheduleReport
                report={editReportData}
                onReceiveProps={handleEditClose}
              />
            }
            onReceiveProps={handleEditClose}
            isFooter={false}
          />
        </div>
      )}

      {/* Report Details Side Drawer */}
      {detailsModalOpen && selectedReport && (
        <RightSideBar
          componentLayout={{
            title: "Report Details",
            description: "",
          }}
          componentData={
            <ReportDetailsContent
              report={selectedReport}
              formatReportType={formatReportType}
              formatDate={formatDate}
              formatStatus={formatStatus}
              formatFieldName={formatFieldName}
              formatFilterType={formatFilterType}
              formatFilterOperation={formatFilterOperation}
              getFilterValueDisplayName={getFilterValueDisplayName}
            />
          }
          onReceiveProps={handleCloseDetailsModal}
          isFooter={false}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        keepMounted
        onClose={handleDeleteClose}
        aria-describedby="alert-dialog"
        style={{ marginTop: "-26%" }}
      >
        <Box sx={{ width: "395px" }}>
          <DialogTitle className="diabtn">Confirm</DialogTitle>
          <DialogContent>
            <DialogContentText className="diabtnjob" id="alert-dialog">
              <p className="btntext">Are you sure you want to delete ?</p>
            </DialogContentText>
          </DialogContent>
          <DialogActions className="alert-actionbtns">
            <Button onClick={handleDeleteClose}>
              <Link href="#" color="#000" underline="always">
                Cancel
              </Link>
            </Button>
            <Button onClick={handleDeleteConfirm}>Delete</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

// Report Details Content Component for Side Drawer
const ReportDetailsContent = ({
  report,
  formatReportType,
  formatDate,
  formatStatus,
  formatFieldName,
  formatFilterType,
  formatFilterOperation,
  getFilterValueDisplayName,
}) => {
  return (
    <div className="report-details-drawer-content">
      {/* Basic Info Section */}
      <div className="details-section">
        <h3 className="details-section-title">Basic Information</h3>
        <div className="details-grid">
          <div className="details-item">
            <span className="details-label">Report Name</span>
            <span className="details-value">{report.reportName || "-"}</span>
          </div>
          <div className="details-item">
            <span className="details-label">Category</span>
            <span className="details-value">
              {formatReportType(report.reportType)}
            </span>
          </div>
          <div className="details-item">
            <span className="details-label">Date Range</span>
            <span className="details-value">
              {formatDate(report.startDate)} - {formatDate(report.endDate)}
            </span>
          </div>
          <div className="details-item">
            <span className="details-label">Status</span>
            <span
              className={`status-badge ${(report.status || "pending").toLowerCase()}`}
            >
              {formatStatus(report.status)}
            </span>
          </div>
          <div className="details-item">
            <span className="details-label">Created By</span>
            <span className="details-value">{report.createdBy || "-"}</span>
          </div>
          <div className="details-item">
            <span className="details-label">Created At</span>
            <span className="details-value">
              {formatDate(report.createdAt)}
            </span>
          </div>
        </div>
      </div>

      {/* Selected Fields Section */}
      <div className="details-section">
        <h3 className="details-section-title">
          Selected Fields ({report.selectedFields?.length || 0})
        </h3>
        <div className="details-chips">
          {report.selectedFields?.map((field, index) => (
            <Chip
              key={index}
              label={formatFieldName(field, report.reportType)}
              size="small"
              className="field-chip"
            />
          ))}
          {(!report.selectedFields || report.selectedFields.length === 0) && (
            <span className="no-data-text">No fields selected</span>
          )}
        </div>
      </div>

      {/* Applied Filters Section */}
      <div className="details-section">
        <h3 className="details-section-title">
          Applied Filters ({report.criteria?.length || 0})
        </h3>
        {report.criteria?.length > 0 ? (
          <div className="filters-table-wrapper">
            <table className="filters-table">
              <thead>
                <tr>
                  <th>Filter</th>
                  <th>Condition</th>
                  <th>Values</th>
                </tr>
              </thead>
              <tbody>
                {report.criteria.map((criterion, index) => (
                  <tr key={index}>
                    <td className="filter-name">
                      {formatFilterType(criterion.filterType)}
                    </td>
                    <td className="filter-condition">
                      <span className="condition-badge">
                        {formatFilterOperation(criterion.filterOperation)}
                      </span>
                    </td>
                    <td className="filter-values">
                      {criterion.filterValues?.map((value, vIndex) => (
                        <Chip
                          key={vIndex}
                          label={getFilterValueDisplayName(
                            criterion.filterType,
                            value,
                          )}
                          size="small"
                          className="filter-value-chip"
                        />
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <span className="no-data-text">No filters applied</span>
        )}
      </div>

      {/* Error Section (if any) */}
      {report.errors && (
        <div className="details-section error-section">
          <h3 className="details-section-title">Errors</h3>
          <p className="error-text">{report.errors}</p>
        </div>
      )}
    </div>
  );
};

export default ReportCentre;
